import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, ErrorEventNotificationSummary } from '../services/api';
import {
  buildDesktopNotification,
  detectNewActivity,
  DesktopNotificationContent,
  NOTIFICATION_POLL_INTERVAL_MS,
} from './errorEventNotificationLogic';
import { updateFaviconBadge } from '../utils/faviconBadge';

/** localStorage-Key für den Opt-in-Schalter „Desktop-Benachrichtigungen". */
const DESKTOP_NOTIFICATIONS_STORAGE_KEY = 'admin_desktop_error_notifications';

/** Browser-Permission-Zustand, erweitert um Browser ohne Notification-API. */
export type DesktopNotificationPermission = NotificationPermission | 'unsupported';

/** Liest den aktuellen Notification-Permission-Zustand des Browsers. */
function readPermission(): DesktopNotificationPermission {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Liest den persistierten Opt-in-Schalter — nur wirksam, solange die Permission `granted` ist. */
function readStoredDesktopEnabled(): boolean {
  return localStorage.getItem(DESKTOP_NOTIFICATIONS_STORAGE_KEY) === 'true';
}

export interface UseErrorEventNotificationsOptions {
  /** Polling nur für Super-Admins aktivieren — die Summary-API ist Super-Admin-only. */
  enabled: boolean;
  /** Navigation im Portal (react-router `navigate`) — Ziel beim Klick auf die Desktop-Benachrichtigung. */
  onNavigate: (path: string) => void;
}

export interface UseErrorEventNotificationsResult {
  /** Letzter erfolgreicher Poll-Stand — `null` bis zum ersten Erfolg. */
  summary: ErrorEventNotificationSummary | null;
  /** Zeitpunkt des letzten erfolgreichen Polls. */
  lastCheckedAt: Date | null;
  /** Opt-in-Schalter, nur zusammen mit Permission `granted` wirksam. */
  desktopEnabled: boolean;
  permission: DesktopNotificationPermission;
  /** Fragt die Browser-Permission an (braucht eine User-Geste) und aktiviert bei Erfolg. */
  enableDesktopNotifications: () => Promise<void>;
  disableDesktopNotifications: () => void;
}

/**
 * Pollt die Error-Event-Zusammenfassung (beim Mount + alle 30 s) und feuert
 * bei neuer Aktivität eine native Desktop-Benachrichtigung, sofern der Admin
 * sie aktiviert hat. Poll-Fehler sind bewusst still (kein Toast alle 30 s) —
 * das Badge behält dann den letzten bekannten Stand.
 */
export function useErrorEventNotifications({
  enabled,
  onNavigate,
}: UseErrorEventNotificationsOptions): UseErrorEventNotificationsResult {
  const [summary, setSummary] = useState<ErrorEventNotificationSummary | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [permission, setPermission] = useState<DesktopNotificationPermission>(() => readPermission());
  const [desktopEnabled, setDesktopEnabled] = useState<boolean>(() => readStoredDesktopEnabled());

  // Refs statt State-Deps im Intervall-Closure — der Timer soll beim Umschalten nicht neu starten.
  const previousSummaryRef = useRef<ErrorEventNotificationSummary | null>(null);
  const desktopEnabledRef = useRef(desktopEnabled);
  desktopEnabledRef.current = desktopEnabled;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  const fireDesktopNotification = useCallback((content: DesktopNotificationContent) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    const notification = new Notification(content.title, {
      body: content.body,
      tag: 'callbert-admin-error-events',
    });
    notification.onclick = () => {
      window.focus();
      onNavigateRef.current(content.target);
      notification.close();
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const next = await apiService.getErrorEventNotificationSummary();
        if (cancelled) return;

        const decision = detectNewActivity(previousSummaryRef.current, next);
        previousSummaryRef.current = next;
        setSummary(next);
        setLastCheckedAt(new Date());
        updateFaviconBadge(next.openTotal + (next.dlqTotal ?? 0));

        if (desktopEnabledRef.current) {
          const content = buildDesktopNotification(next, decision);
          if (content) fireDesktopNotification(content);
        }
      } catch {
        // Still: Polling-Fehler sollen die Admin-Arbeit nicht mit Toasts fluten.
      }
    };

    void poll();
    const intervalId = window.setInterval(() => void poll(), NOTIFICATION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      // Ohne aktives Polling (Logout/Unmount) soll der Tab keinen veralteten Zähler zeigen.
      updateFaviconBadge(0);
    };
  }, [enabled, fireDesktopNotification]);

  const enableDesktopNotifications = useCallback(async () => {
    if (!('Notification' in window)) return;

    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      localStorage.setItem(DESKTOP_NOTIFICATIONS_STORAGE_KEY, 'true');
      setDesktopEnabled(true);
    }
  }, []);

  const disableDesktopNotifications = useCallback(() => {
    localStorage.setItem(DESKTOP_NOTIFICATIONS_STORAGE_KEY, 'false');
    setDesktopEnabled(false);
  }, []);

  return {
    summary,
    lastCheckedAt,
    desktopEnabled,
    permission,
    enableDesktopNotifications,
    disableDesktopNotifications,
  };
}
